import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { goToCoachProfile } from '../utils/coachNav';
import type { Lesson, PendingLessonUpload } from '../types/lesson';
import type { Order } from '../types/order';
import { DETAIL_COLUMN, DetailTitleRow } from './detailLayout';
import { useUserIdentity } from '../hooks/useUserIdentity';
import { useUserPendingUploads } from '../hooks/usePendingUploads';
import { lessonTitle } from './PlayerLessonCard';
import DetailSection from './DetailSection';
import { Skeleton, SkeletonGroup } from './Skeleton';
import LessonStatusIcon from './LessonStatusIcon';
import { BTN_PRIMARY, BTN_SECONDARY_SM_ON_PAGE } from './buttonStyles';
import { formatLessonDate } from '../utils/lessonDate';
import { lessonStateCopy } from '../utils/lessonStateCopy';

/* The screen renders from one of two sources, never both.
   A booked lesson has an order and a lesson document. A booking whose upload
   has finished but whose lesson has not been created yet has neither — only
   the pendingLessonUploads doc — and that window lasts as long as a transcode.
   Rather than a second component rendering the same screen from the second
   source (which is how the wrong-video bug happened: two holders of one truth
   drifting apart), both are mapped to one view model below and the layout is
   written once.

   The union makes mixing them a type error while leaving every existing call
   site, which passes order + lesson, valid unchanged. */
type WaitingForLessonSource =
  | { order: Order; lesson: Lesson; pendingUpload?: never }
  | { order?: never; lesson?: never; pendingUpload: PendingLessonUpload };

type WaitingForLessonProps = WaitingForLessonSource & {
  /** All player lessons — used to determine first vs returning */
  allLessons: Lesson[];
  onBack?: () => void;
  /** When set, a reserved lesson with no player video shows an upload CTA instead of the waiting state. */
  onUpload?: () => void;
  /** 'page' (default) is the full-screen route treatment: a 760px centred
   *  column on the grey app background, with its own back button. 'panel'
   *  fills a two-pane container instead — full width, white, no back button,
   *  since the list beside it is already the way back. Padding is identical
   *  in both. */
  variant?: 'page' | 'panel';
};

/** Even rhythm down the header: title -> name row -> status. Was 12 then 24. */
const HEADER_GAP = 16;

export default function WaitingForLesson({ order, lesson, pendingUpload, allLessons: _allLessons, onBack, onUpload, variant = 'page' }: WaitingForLessonProps) {
  const isPanel = variant === 'panel';
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  /* One view model, two sources. Everything below renders from this and never
     touches `lesson` or `pendingUpload` again, so the two states cannot drift
     into different layouts. */
  const vm = pendingUpload
    ? {
        coachId: pendingUpload.coachId,
        coachName: undefined as string | undefined,
        coachAvatarUrl: undefined as string | undefined,
        conversationId: undefined as string | undefined,
        title: pendingUpload.offeringTitle,
        createdAt: pendingUpload.createdAt,
        /* Known from the moment the player pressed submit — it is written to
           the upload doc at creation. No skeleton needed for it. */
        note: (pendingUpload.playerMessage ?? '').trim(),
        videoUrl: undefined as string | undefined,
        /* The lesson does not exist yet, so it cannot be reserved-without-video
           and it cannot be awaiting review. It is mid-pipeline. */
        isReservedNoVideo: false,
        isTranscoding: true,
      }
    : {
        coachId: lesson!.coachId ?? order!.coachId,
        coachName: order!.coachName,
        coachAvatarUrl: order!.coachAvatarUrl,
        conversationId: order!.conversationId,
        title: lessonTitle(lesson!, order!),
        createdAt: lesson!.createdAt,
        note: (lesson!.playerMessage ?? order!.playerMessage ?? '').trim(),
        videoUrl: lesson!.playerVideoUrl,
        isReservedNoVideo:
          lesson!.status === 'reserved' && !lesson!.playerVideoUrl,
        isTranscoding: false,
      };

  /* An upload naming THIS lesson and still in flight.
     The transcoding state used to depend on the pendingUpload branch of the
     view model, which only exists when Lessons pushes a pending detail from
     ?await= after a new booking. Uploading to a lesson that already exists is
     not a navigation, so nothing set it and the pane kept offering an Upload
     button for a file that was already on its way.

     Requires a pendingLessonUploads doc that names this lesson AND is in one
     of the three in-flight statuses, so a reserved lesson nobody has uploaded
     to has no match and keeps its upload CTA. */
  const { uploads } = useUserPendingUploads();
  const uploadInFlight = lesson
    ? uploads.find(u =>
        (u.lessonId === lesson.id || u.existingLessonId === lesson.id) &&
        (u.status === 'uploading' || u.status === 'uploaded' || u.status === 'processing'))
    : undefined;

  const liveCoach = useUserIdentity(vm.coachId, 'coach');
  const coachDisplayName = liveCoach?.name ?? vm.coachName ?? '';
  const coachFirstName = coachDisplayName.split(' ')[0] || 'your coach';
  const coachInitials = (coachDisplayName || 'C')
    .split(' ')
    .filter(Boolean)
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const coachAvatarUrl = liveCoach?.avatarUrl ?? vm.coachAvatarUrl;
  const coachId = vm.coachId;

  const handleMessage = () => {
    navigate(vm.conversationId ? `/inbox/${vm.conversationId}` : '/inbox');
  };

  // Submission timestamp — month + day, matching the list rows.
  const submittedDate = vm.createdAt
    ? formatLessonDate(vm.createdAt)
    : '';

  // The player's own note. The client-side booking path keeps it on the order
  // ("playerMessage stays on order" in services/orders.ts); the upload Cloud
  // Function writes it to both. Lesson first, order as the fallback.
  const playerNote = vm.note;

  const playerVideoUrl = vm.videoUrl;
  /* An upload in flight outranks "reserved with no video": the file is on its
     way, so offering the Upload button again would invite a second one. */
  const isReservedNoVideo = vm.isReservedNoVideo && !uploadInFlight;
  /* The lesson exists and the player has paid, but the video has not finished
     processing yet. Distinct from isReservedNoVideo, which is a lesson waiting
     for the player to upload at all — that one should show the upload CTA, not
     a placeholder for a file nobody has chosen. */
  /* Either route into the state: the ?await= pending pane, or an upload in
     flight against a lesson that already exists. */
  const isProcessingVideo = vm.isTranscoding || !!uploadInFlight;

  /* One source for both screens. No lessonNumber: a single lesson has nothing
     to name, and the shared copy leaves the clause out when it is absent. */
  const copy = isProcessingVideo
    /* Its own state, not a variant of "awaiting review".
       This pane can be the only thing the player sees for the length of a
       transcode, so it has to answer the three questions they actually have:
       did it work, what is happening, and do I have to sit here. A bare
       skeleton answers none of them and reads as a page that failed to load.
       Naming the state and saying they can leave turns the same wait into a
       deliberate screen. */
    ? {
        label: 'Uploaded',
        short: 'Your video is uploading to your coach.',
        long: `Your video is in. We are preparing it for ${coachFirstName}, which usually takes a few minutes. You can close this and we will let you know when it is ready.`,
      }
    : lessonStateCopy(
        isReservedNoVideo ? 'ready_to_submit' : 'awaiting_review',
        { coachFirstName },
      );

  return (
    <div className={`flex flex-col h-full overflow-y-auto${isPanel ? '' : ' bg-db-bg'}`}>
      <div className={`py-6 md:py-8 pt-5 md:pt-8 ${DETAIL_COLUMN}`}>


        {/* ── Header ─────────────────────────────────────────────────── */}
        <DetailTitleRow
          title={vm.title}
          onBack={isPanel ? undefined : onBack}
          sticky={!isPanel}
        />

        {/* One row at every width. It used to stack on mobile, which put
            Message on a line of its own and broke the header into three
            disconnected bands. */}
        <div className="flex flex-row items-center justify-between gap-3" style={{ marginTop: HEADER_GAP }}>
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              title={coachDisplayName || 'Coach'}
              onClick={() => goToCoachProfile(coachId, currentUser?.uid ?? undefined)}
              className="w-12 h-12 rounded-full flex items-center justify-center overflow-hidden text-[16px] font-bold text-white flex-shrink-0 border-none p-0 cursor-pointer"
              style={{ background: '#54C3EA' }}
            >
              {coachAvatarUrl
                ? <img src={coachAvatarUrl} alt={coachDisplayName} className="w-full h-full object-cover" />
                : coachInitials}
            </button>
            {/* Stacked below md, one row from md. The dot is a separator, so it
                only renders when there is something on either side of it: with
                `flex-wrap` the date used to drop to a second line and start it
                with an orphaned "· ". At 375 there is no arrangement that fits
                the name, the date and the Message pill on one line, so the
                stack is deliberate rather than a wrap.
                The column is 20 + 18 = 38, still under the 48 avatar, so the
                row's height and everything measured against it are unchanged. */}
            <div className="min-w-0 flex flex-col md:flex-row md:items-center md:gap-x-3">
              <span className="text-[14px] leading-[20px] text-[#1C1C1C] truncate">{coachDisplayName}</span>
              {submittedDate && (
                <span className="text-[14px] leading-[18px] md:leading-[20px] text-db-text-2 whitespace-nowrap">
                  <span className="hidden md:inline">· </span>Submitted {submittedDate}
                </span>
              )}
            </div>
          </div>

          {/* Pill. Below md it drops to its own row so it stops colliding
              with the submitted date; from md it sits right on the meta row. */}
          <button
            data-testid="message-coach-btn"
            onClick={handleMessage}
            className={`${BTN_SECONDARY_SM_ON_PAGE} flex-shrink-0`}
          >
            <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Message {coachFirstName}
          </button>
        </div>

        {/* ── Status ─────────────────────────────────────────────────── */}
        {/* Same label the list row uses, so one state has one name. */}
        <div style={{ marginTop: HEADER_GAP }}>
          {copy && (
            <div className="flex items-center" style={{ gap: 8 }}>
              <LessonStatusIcon label={copy.label} />
              <span className="text-[13px] leading-[18px] font-semibold text-[#1C1C1C]">{copy.label}</span>
            </div>
          )}
          {isReservedNoVideo ? (
            <>
              <p className="text-[13px] text-db-text-2" style={{ marginTop: 6 }}>
                {copy?.long}
              </p>
              {onUpload && (
                <button
                  data-testid="upload-video-btn"
                  onClick={onUpload}
                  className={`${BTN_PRIMARY} w-full mt-4`}
                  style={{ width: 'auto', paddingLeft: 20, paddingRight: 20 }}
                >
                  <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="17 8 12 3 7 8" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="12" y1="3" x2="12" y2="15" strokeLinecap="round"/>
                  </svg>
                  Upload Your Video
                </button>
              )}
            </>
          ) : (
            <>
              {/* Split on the breakpoint, not on `variant`: desktop passes
                  variant="page" too once the user has drilled past the root of
                  the detail stack, so isPanel would have shortened the copy on
                  desktop as well. md is 768, the same boundary ResponsiveContext
                  uses for isMobile, and this file already sizes on it.

                  The status line above already names the state, so the short
                  form carries only the timing. */}
              <p className="md:hidden text-[13px] leading-[18px] text-[rgba(60,60,67,0.6)]" style={{ marginTop: 6 }}>
                {copy?.short}
              </p>
              {/* Capped, alone. At the column's 896 this ran as one 135-character
                  line — roughly twice a comfortable measure. 520 keeps it near
                  80 characters and wraps to two lines. The cap is on the
                  paragraph, not the column, so the video and the rows beside it
                  still get the full width. */}
              <p className="hidden md:block max-w-[520px] text-[13px] text-db-text-2" style={{ marginTop: 6 }}>
                {copy?.long}
              </p>
            </>
          )}
        </div>

        {/* ── Your video ─────────────────────────────────────────────── */}
        {/* Processing: the same section, with the picture stood in for.
            Before this the whole block was simply absent until the URL
            arrived, so the screen grew by the height of a 16:9 video the
            moment processing finished — a jump of several hundred pixels
            under whatever the player was reading.

            No jump now, because the placeholder is the real box: same
            marginTop 32, same header, same `w-full` with `aspectRatio 16/9`
            and `borderRadius 12` as the <video> that replaces it. It is the
            same element shape, so the swap is a repaint rather than a
            reflow. */}
        {isProcessingVideo && (
          <div style={{ marginTop: 32 }}>
            <p className="text-[13px] leading-[18px] font-semibold uppercase text-[rgba(60,60,67,0.6)]" style={{ letterSpacing: '0.5px', marginBottom: 16 }}>
              Your video
            </p>
            <SkeletonGroup label="Your video is still processing">
              {/* The aspect ratio lives on a wrapper, not on Skeleton: Skeleton
                  only takes width/height, so height="auto" would have collapsed
                  it to nothing. The wrapper holds the 16/9 box the <video>
                  occupies and the block fills it. */}
              <div className="w-full" style={{ aspectRatio: '16 / 9' }}>
                <Skeleton width="100%" height="100%" radius={12} />
              </div>
            </SkeletonGroup>
          </div>
        )}
        {playerVideoUrl && (
          <div style={{ marginTop: 32 }}>
            <p className="text-[13px] leading-[18px] font-semibold uppercase text-[rgba(60,60,67,0.6)]" style={{ letterSpacing: '0.5px', marginBottom: 16 }}>
              Your video
            </p>
            {/* Same player frame the delivered screen uses for the breakdown,
                so the two surfaces read as peers. */}
            <video
              src={playerVideoUrl}
              controls
              controlsList="nodownload"
              preload="metadata"
              className="w-full bg-black"
              style={{ aspectRatio: '16 / 9', borderRadius: 12 }}
              /* No crossOrigin: display-only. CORS-mode loading fails on
                 iPhone Safari against our bucket (Content-Range not exposed)
                 — see the same removal on LessonDeliveredDetail. */
            />
          </div>
        )}

        {/* ── The player's note ──────────────────────────────────────── */}
        {/* No skeleton for the note: playerMessage is written to the upload
            doc the moment the player presses submit, so it is known for the
            whole of this window and renders for real above. */}
        {playerNote && (
          <DetailSection className="mt-8" header={`Your note to ${coachFirstName}`}>
            <p className="text-[15px] leading-[20px] text-[#1C1C1C] whitespace-pre-wrap">
              {playerNote}
            </p>
          </DetailSection>
        )}

      </div>
    </div>
  );
}
